import * as React from "react";
import { createTheme, styled } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PetsIcon from "@mui/icons-material/Pets";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCut from "@mui/icons-material/ContentCut";
import { AppProvider, type Navigation, Router } from "@toolpad/core";
import type { Session } from "@toolpad/core";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { PageContainer } from "@toolpad/core/PageContainer";
import Grid from "@mui/material/Grid";
import { blue, orange, purple } from "@mui/material/colors";
import {
  Add,
  EnhancedEncryption,
  Person,
  Store,
  Pets,
  Notifications,
} from "@mui/icons-material";
import PetModal from "../components/ModalNewPet";
import ModalNewAtendimento from "../components/ModalNewAtendimento";
import ModalNewVenda from "../components/ModalNewVenda";
import {
  Box,
  Container,
  Fab,
  InputAdornment,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Pagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UserProfile from "../components/UserProfile";
import NotificationsPage from "./Notifications";
import AgendaPage from "./Agenda";
import ServicosEmAndamentoPage from "./ServicosEmAndamento";
import NotificationHeader from "../components/NotificationHeader";
import { ThemeSwitcher } from "@toolpad/core";

import EmptyState from "../components/EmptyState";
import MiniPetCard from "../components/MiniPetCard";
import InfoCard from "../components/InfoCard";
import { IPedido, IPetPopulated } from "../types";
import PetDetails from "../components/PetDetails";
import { getUserFromLocalStorage, ISession, useLogout } from "../utils";
import { getPedidos, getPets } from "../services";
import SearchIcon from "@mui/icons-material/Search";
import ModalDetalhesPedido from "../components/ModalDetalhesPedido";
import ModalEditarPedido from "../components/ModalEditarPedido";
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
} from "@mui/material";
import { Edit } from "@mui/icons-material";

const NAVIGATION: Navigation = [
  {
    segment: "home",
    icon: <DashboardIcon />,
  },
  {
    segment: "pets",
    icon: <PetsIcon />,
  },
  {
    segment: "agenda",
    title: "agenda",
    icon: <CalendarMonthIcon />,
  },
  {
    segment: "servicos",
    title: "serviços",
    icon: <ContentCut />,
  },
  {
    segment: "vendas",
    icon: <Store />,
  },
  {
    segment: "atendimentos",
    icon: <EnhancedEncryption />,
  },
  {
    segment: "notifications",
    title: "notificações",
    icon: <Notifications />,
  },
  {
    segment: "perfil",
    icon: <Person />,
  },
];

const customTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: "data-toolpad-color-scheme",
  },
  colorSchemes: {
    light: {
      palette: {
        primary: orange,
        background: {
          default: "#F7F7F7",
          paper: "#F5F5F5",
        },
      },
    },
    dark: {
      palette: {
        primary: orange,
        background: {
          default: "#100600",
          paper: "#100600",
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
});

function useDemoRouter(initialPath: string): Router {
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path: string | URL) => setPathname(String(path)),
    };
  }, [pathname]);

  return router;
}

const Skeleton = styled("div")<{ height: number }>(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

export default function DashboardLayoutBasic(props: any) {
  const { window } = props;
  const router = useDemoRouter("/home");
  const demoWindow = window ? window() : undefined;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(
    "(max-height: 768px) and (max-width: 1280px)"
  );
  // Notification context is used in CustomToolbarActions

  // Custom toolbar component with notification header and theme switcher
  const CustomToolbarActions = () => {
    const navigate = (path: string) => {
      router.navigate(path);
    };

    return (
      <React.Fragment>
        <NotificationHeader
          onNotificationClick={() => navigate("/notifications")}
        />
        <ThemeSwitcher />
        {/* <Account /> */}
      </React.Fragment>
    );
  };
  const [session, setSession] = React.useState<ISession | null>(null);
  const [authSession, setAuthSession] = React.useState<Session | null>({
    user: {
      name: session?.userERP.NomeFantasia || "",
      email: session?.userERP.Email || "",
      image:
        "https://img.freepik.com/free-photo/view-adorable-3d-cat_23-2150473714.jpg",
    },
  });
  const [petDetails, setPetDetails] = React.useState(false);
  const [petInfos, setPetInfos] = React.useState<IPetPopulated | null>(null);
  const [modalAddPet, setModalAddPet] = React.useState(false);
  const [modalAddAtendimento, setModalAddAtendimento] = React.useState(false);
  const [modalAddVenda, setModalAddVenda] = React.useState(false);
  const [modalDetalhesPedido, setModalDetalhesPedido] = React.useState(false);
  const [modalEditarPedido, setModalEditarPedido] = React.useState(false);
  const [pedidoSelecionado, setPedidoSelecionado] =
    React.useState<IPedido | null>(null);
  const [pets, setPets] = React.useState<IPetPopulated[]>([]);
  const [attachment, setAttachment] = React.useState<IPedido[]>([]);
  const [attachmentUser, setAttachmentUser] = React.useState<IPedido[]>([]);
  const [vendas, setVendas] = React.useState<IPedido[]>([]);
  const [vendasUser, setVendasUser] = React.useState<IPedido[]>([]);
  // Novos estados para manter todos os pedidos (sem filtro de categoria)
  const [todosPedidos, setTodosPedidos] = React.useState<IPedido[]>([]);
  const [todosPedidosUser, setTodosPedidosUser] = React.useState<IPedido[]>([]);
  const logout = useLogout();
  // const [loadingPedidos, setLoadingPedidos] = React.useState(false); // removido por não exibir spinner
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchTermAttach, setSearchTermAttach] = React.useState("");
  const [searchTermVendas, setSearchTermVendas] = React.useState("");
  // Estados para filtros
  const [mostrarTodosAtendimentos, setMostrarTodosAtendimentos] =
    React.useState(false);
  // Estados para paginação de pets
  const [petsPage, setPetsPage] = React.useState(1);
  const petsPerPage = isSmallScreen ? 9 : isMobile ? 6 : 9; // 4 pets em small screen (1 coluna), 6 em mobile, 9 em desktop
  // Estados para paginação de vendas e atendimentos
  const [vendasPage, setVendasPage] = React.useState(1);
  const [atendimentosPage, setAtendimentosPage] = React.useState(1);
  const vendasPerPage = 10; // Número de vendas por página
  const atendimentosPerPage = 10; // Número de atendimentos por página

  // Obter o segmento atual da navegação
  const titlePage = {
    home: "Dashboard",
    pets: "",
    agenda: "",
    servicos: "",
    vendas: "",
    atendimentos: "",
    notifications: "",
    perfil: "",
  } as const;

  type Segment = keyof typeof titlePage;

  const currentSegment = (router.pathname.split("/")[1] || "home") as Segment;

  const authentication = React.useMemo(() => {
    return {
      signIn: () => {
        setAuthSession({
          user: {
            name: session?.userERP.NomeFantasia || "",
            email: session?.userERP.Email || "",
            image:
              "https://img.freepik.com/free-photo/view-adorable-3d-cat_23-2150473714.jpg",
          },
        });
      },
      signOut: () => {
        logout();
      },
    };
  }, [session?.userERP.NomeFantasia, session?.userERP.Email, logout]);

  const cleanId = (dirtyId: string): string => {
    return decodeURIComponent(dirtyId).replace(/^"+|"+$/g, "");
  };

  const clearId = cleanId(session?.userId || "");

  const fetchAllPedidos = React.useCallback(async () => {
    try {
      const pedidosData = await getPedidos();
      const allPedidos = pedidosData || [];

      const ordered = allPedidos.slice().sort((a: any, b: any) => {
        // 1. Ordenação primária: Codigo (sequencial) desc
        const codeA =
          typeof a.Codigo === "number" ? a.Codigo : parseInt(a.Codigo, 10);
        const codeB =
          typeof b.Codigo === "number" ? b.Codigo : parseInt(b.Codigo, 10);
        if (!isNaN(codeA) && !isNaN(codeB) && codeA !== codeB) {
          return codeB - codeA;
        }
        // 2. Fallback para data mais recente
        const da = new Date(
          a.Data ||
            a.DataEnvio ||
            (a as any).DataAbertura ||
            (a as any).DataAprovacaoPedido ||
            0
        ).getTime();
        const db = new Date(
          b.Data ||
            b.DataEnvio ||
            (b as any).DataAbertura ||
            (b as any).DataAprovacaoPedido ||
            0
        ).getTime();
        return db - da;
      });

      // Salvar todos os pedidos
      setTodosPedidos(ordered);
      setTodosPedidosUser(ordered);

      // Separar por categoria
      const atendimentos = ordered.filter(
        (pedido: IPedido) => pedido.Categoria === "Venda de Serviços"
      );
      const vendasProdutos = ordered.filter(
        (pedido: IPedido) => pedido.Categoria === "Venda de Produtos"
      );

      // Atualizar ambos os estados (tech e user) com os mesmos dados
      setAttachment(atendimentos);
      setAttachmentUser(atendimentos);
      setVendas(vendasProdutos);
      setVendasUser(vendasProdutos);
    } catch (error) {
      setTodosPedidos([]);
      setTodosPedidosUser([]);
      setAttachment([]);
      setAttachmentUser([]);
      setVendas([]);
      setVendasUser([]);
    }
  }, []);

  const fetchPets = React.useCallback(async () => {
    try {
      // loading flag removido
      if (!session) {
        return;
      }

      if (session.userERP?.Tecnico) {
        const petsData = await getPets();

        const ordered = (petsData || [])
          .slice()
          .sort((a: IPetPopulated, b: IPetPopulated) =>
            a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
          );
        setPets(ordered);
      } else if (session.userId) {
        const petsData = await getPets(clearId);

        const ordered = (petsData || [])
          .slice()
          .sort((a: IPetPopulated, b: IPetPopulated) =>
            a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
          );
        setPets(ordered);
      }
    } catch (error) {
    } finally {
      // loading flag removido
    }
  }, [session, clearId]);

  React.useEffect(() => {
    const user = getUserFromLocalStorage();
    if (user && typeof user.userERP === "object" && user.userERP !== null) {
      setSession(user);
    } else {
      setSession(null);
    }
  }, []);

  React.useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  React.useEffect(() => {
    if (!session) return;
    fetchAllPedidos();
  }, [session, fetchAllPedidos]);

  const filteredPets = React.useMemo(() => {
    if (!searchTerm) return pets;

    const lowercasedTerm = searchTerm.toLowerCase();

    return pets.filter((pet) => {
      const petNameMatches = pet.name.toLowerCase().includes(lowercasedTerm);
      const tutorNameMatches = pet.tutor.name
        .toLowerCase()
        .includes(lowercasedTerm);

      return petNameMatches || tutorNameMatches;
    });
  }, [pets, searchTerm]);

  // Paginação de pets
  const paginatedPets = React.useMemo(() => {
    const startIndex = (petsPage - 1) * petsPerPage;
    const endIndex = startIndex + petsPerPage;
    return filteredPets.slice(startIndex, endIndex);
  }, [filteredPets, petsPage, petsPerPage]);

  const totalPetsPages = React.useMemo(() => {
    return Math.ceil(filteredPets.length / petsPerPage);
  }, [filteredPets.length, petsPerPage]);

  // Reset para página 1 quando o termo de busca mudar
  React.useEffect(() => {
    setPetsPage(1);
  }, [searchTerm]);

  // Reset para página 1 quando o tamanho da tela mudar
  React.useEffect(() => {
    setPetsPage(1);
  }, [isMobile]);

  const filteredattachment = React.useMemo(() => {
    // Escolher dados baseado no filtro e se é técnico ou usuário
    let dataToFilter;
    if (mostrarTodosAtendimentos) {
      // Mostrar todos os pedidos
      dataToFilter = session?.userERP.Tecnico ? todosPedidos : todosPedidosUser;
    } else {
      // Mostrar apenas atendimentos (Venda de Serviços)
      dataToFilter = session?.userERP.Tecnico ? attachment : attachmentUser;
    }

    if (!searchTermAttach) return dataToFilter;

    const lowercasedTerm = searchTermAttach.toLowerCase();

    return dataToFilter.filter((attach) => {
      // Busca na descrição do pedido
      const descricaoMatches =
        attach.Descricao?.toLowerCase().includes(lowercasedTerm);

      // Busca no nome do cliente
      const clienteMatches =
        attach.Cliente?.toLowerCase().includes(lowercasedTerm);

      // Busca no código do pedido
      const codigoMatches = attach.Codigo?.toString()
        .toLowerCase()
        .includes(lowercasedTerm);

      // Busca na data do pedido
      const dataMatches = attach.Data?.toLowerCase().includes(lowercasedTerm);

      // Busca nos produtos/serviços do pedido
      const produtosMatches =
        attach.Items?.some(
          (item: any) =>
            item.Descricao?.toLowerCase().includes(lowercasedTerm) ||
            item.Nome?.toLowerCase().includes(lowercasedTerm)
        ) || false;

      return (
        descricaoMatches ||
        clienteMatches ||
        codigoMatches ||
        dataMatches ||
        produtosMatches
      );
    });
  }, [
    attachment,
    attachmentUser,
    todosPedidos,
    todosPedidosUser,
    searchTermAttach,
    mostrarTodosAtendimentos,
    session?.userERP.Tecnico,
  ]);

  const filteredVendas = React.useMemo(() => {
    if (!searchTermVendas) return vendas;

    const lowercasedTerm = searchTermVendas.toLowerCase();

    return vendas.filter((venda) => {
      // Busca na descrição do pedido
      const descricaoMatches =
        venda.Descricao?.toLowerCase().includes(lowercasedTerm);

      // Busca no nome do cliente
      const clienteMatches =
        venda.Cliente?.toLowerCase().includes(lowercasedTerm);

      // Busca no código do pedido
      const codigoMatches = venda.Codigo?.toString()
        .toLowerCase()
        .includes(lowercasedTerm);

      // Busca na data do pedido
      const dataMatches = venda.Data?.toLowerCase().includes(lowercasedTerm);

      // Busca nos produtos da venda
      const produtosMatches =
        venda.Items?.some(
          (item: any) =>
            item.Descricao?.toLowerCase().includes(lowercasedTerm) ||
            item.Nome?.toLowerCase().includes(lowercasedTerm)
        ) || false;

      return (
        descricaoMatches ||
        clienteMatches ||
        codigoMatches ||
        dataMatches ||
        produtosMatches
      );
    });
  }, [vendas, searchTermVendas]);

  // Paginação de vendas
  const paginatedVendas = React.useMemo(() => {
    const startIndex = (vendasPage - 1) * vendasPerPage;
    const endIndex = startIndex + vendasPerPage;
    return filteredVendas.slice(startIndex, endIndex);
  }, [filteredVendas, vendasPage, vendasPerPage]);

  const totalVendasPages = React.useMemo(() => {
    return Math.ceil(filteredVendas.length / vendasPerPage);
  }, [filteredVendas.length, vendasPerPage]);

  // Reset para página 1 quando o termo de busca de vendas mudar
  React.useEffect(() => {
    setVendasPage(1);
  }, [searchTermVendas]);

  // Paginação de atendimentos
  const paginatedAttachment = React.useMemo(() => {
    const startIndex = (atendimentosPage - 1) * atendimentosPerPage;
    const endIndex = startIndex + atendimentosPerPage;
    return filteredattachment.slice(startIndex, endIndex);
  }, [filteredattachment, atendimentosPage, atendimentosPerPage]);

  const totalAttachmentPages = React.useMemo(() => {
    return Math.ceil(filteredattachment.length / atendimentosPerPage);
  }, [filteredattachment.length, atendimentosPerPage]);

  // Reset para página 1 quando o termo de busca de atendimentos mudar
  React.useEffect(() => {
    setAtendimentosPage(1);
  }, [searchTermAttach, mostrarTodosAtendimentos]);

  // Combinar todos os pedidos (atendimentos e vendas) para exibir na home
  const todosPedidosParaHome = React.useMemo(() => {
    let atendimentosData, vendasData;

    if (session?.userERP.Tecnico) {
      atendimentosData = attachment || [];
      vendasData = vendas || [];
    } else {
      atendimentosData = attachmentUser || [];
      vendasData = vendasUser || [];
    }

    // Combinar e ordenar por código (mais recente primeiro)
    const combined = [...atendimentosData, ...vendasData].sort((a, b) => {
      const codeA =
        typeof a.Codigo === "number" ? a.Codigo : parseInt(a.Codigo || "0", 10);
      const codeB =
        typeof b.Codigo === "number" ? b.Codigo : parseInt(b.Codigo || "0", 10);
      if (!isNaN(codeA) && !isNaN(codeB) && codeA !== codeB) {
        return codeB - codeA;
      }
      // Fallback para data mais recente
      const da = new Date(
        a.Data ||
          a.DataEnvio ||
          (a as any).DataAbertura ||
          (a as any).DataAprovacaoPedido ||
          0
      ).getTime();
      const db = new Date(
        b.Data ||
          b.DataEnvio ||
          (b as any).DataAbertura ||
          (b as any).DataAprovacaoPedido ||
          0
      ).getTime();
      return db - da;
    });

    return combined;
  }, [
    attachment,
    attachmentUser,
    vendas,
    vendasUser,
    session?.userERP.Tecnico,
  ]);

  // Funções para lidar com modals de detalhes e edição
  const handleAbrirDetalhes = (pedido: IPedido) => {
    setPedidoSelecionado(pedido);
    setModalDetalhesPedido(true);
  };

  const handleFecharDetalhes = () => {
    setModalDetalhesPedido(false);
    setPedidoSelecionado(null);
  };

  const handleAbrirEdicao = (pedido: IPedido) => {
    setPedidoSelecionado(pedido);
    setModalEditarPedido(true);
  };

  const handleFecharEdicao = () => {
    setModalEditarPedido(false);
    setPedidoSelecionado(null);
  };

  return (
    <AppProvider
      session={authSession}
      navigation={NAVIGATION}
      authentication={authentication}
      router={router}
      theme={customTheme}
      window={demoWindow}
      branding={{
        logo: <img src="flowpet-icon-2.png" alt="FlowPet" />,
        title: "FlowPet",
      }}
    >
      <DashboardLayout
        slots={{
          toolbarActions: CustomToolbarActions,
        }}
      >
        <PageContainer
          title={
            titlePage[currentSegment] === ""
              ? ""
              : titlePage[currentSegment] === "Dashboard"
                ? `Olá, ${session?.userERP.NomeFantasia?.split(" ")[0]}`
                : `${titlePage[currentSegment]}`
          }
          breadcrumbs={[]}
          sx={{
            height: "100%",
            overflowY:
              currentSegment === "servicos" || currentSegment === "agenda"
                ? "hidden"
                : "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid
            container
            spacing={1}
            justifyContent={"center"}
            sx={{
              height: "100%",
              overflow: "visible",
            }}
          >
            {/* Conteúdo padrão para todas as páginas */}
            <Grid spacing={12} sx={{ backgroundColor: blue[100] }}>
              <Skeleton height={14} />
            </Grid>
            {currentSegment === "home" && (
              <Container>
                <Grid container>
                  <Grid spacing={4}>
                    <Typography
                      variant={isSmallScreen ? "body1" : "h5"}
                      gutterBottom
                      color="gray"
                    >
                      Resumo:
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "stretch",
                      gap: { xs: 2, sm: 2, md: 2, lg: 2 },
                      margin: 0,
                      width: "100%",
                      px: { xs: 2, sm: 2, md: 0 },
                    }}
                  >
                    {/* Gráfico de Animais */}
                    <Grid
                      size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        px: { xs: 0, sm: 1 },
                      }}
                    >
                      <Box sx={{ width: "100%", maxWidth: 400 }}>
                        <InfoCard
                          value={pets.length}
                          category="Animais"
                          color="#bbdefb"
                        />
                      </Box>
                    </Grid>

                    {/* Gráfico de Atendimentos */}
                    <Grid
                      size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        px: { xs: 0, sm: 1 },
                      }}
                    >
                      <Box sx={{ width: "100%", maxWidth: 400 }}>
                        <InfoCard
                          value={
                            session?.userERP.Tecnico
                              ? attachment.length
                              : attachmentUser.length
                          }
                          category={"Atendimentos"}
                          color="#c8e6c9"
                        />
                      </Box>
                    </Grid>

                    {/* Gráfico de Vendas */}
                    <Grid
                      size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        px: { xs: 0, sm: 1 },
                      }}
                    >
                      <Box sx={{ width: "100%", maxWidth: 400 }}>
                        <InfoCard
                          value={
                            session?.userERP.Tecnico
                              ? vendas.length
                              : vendasUser.length
                          }
                          category={"Vendas"}
                          color="#fff9c4"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid spacing={4}>
                    <Typography
                      variant={isSmallScreen ? "body1" : "h5"}
                      gutterBottom
                      color="gray"
                    >
                      Todos os Atendimentos e Vendas:
                    </Typography>
                  </Grid>
                  <Container
                    sx={{
                      py: isSmallScreen ? 1 : 2,
                      maxHeight: isSmallScreen ? 350 : 500,
                      overflowY: "auto",
                    }}
                  >
                    {todosPedidosParaHome.length > 0 ? (
                      <List>
                        {todosPedidosParaHome.map((pedido, index) => (
                          <ListItem
                            key={`${pedido.ID}-${index}`}
                            sx={{
                              py: isSmallScreen ? 1 : 2,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  bgcolor:
                                    pedido.Categoria === "Venda de Produtos"
                                      ? "success.main"
                                      : "warning.main",
                                  width: isSmallScreen ? 32 : 40,
                                  height: isSmallScreen ? 32 : 40,
                                }}
                              >
                                {pedido.Categoria === "Venda de Produtos" ? (
                                  <Store />
                                ) : (
                                  <Pets />
                                )}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  variant={isSmallScreen ? "body2" : "body1"}
                                  fontWeight="medium"
                                >
                                  {pedido.Data}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography
                                    variant={
                                      isSmallScreen ? "caption" : "body2"
                                    }
                                    color="text.secondary"
                                  >
                                    {`${pedido.Items?.[0]?.Descricao || (pedido.Categoria === "Venda de Produtos" ? "Produto" : "Serviço")} - Código: ${pedido.Codigo}`}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={
                                      pedido.Categoria === "Venda de Produtos"
                                        ? "success.main"
                                        : "warning.main"
                                    }
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    Cliente: {pedido.Cliente}
                                  </Typography>
                                </>
                              }
                              onClick={() => handleAbrirDetalhes(pedido)}
                            />
                            <IconButton
                              edge="end"
                              aria-label="editar"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAbrirEdicao(pedido);
                              }}
                              sx={{ ml: 1 }}
                              size={isSmallScreen ? "small" : "medium"}
                            >
                              <Edit
                                fontSize={isSmallScreen ? "small" : "medium"}
                              />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <EmptyState label="Sem pedidos encontrados" />
                    )}
                  </Container>
                </Grid>
              </Container>
            )}
            {currentSegment === "pets" && (
              <Container>
                {!petDetails && (
                  <>
                    {session?.userERP?.Tecnico && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          alignItems: "center",
                          width: "100%",
                          mb: { xs: 2, sm: isSmallScreen ? 2 : 3 },
                          px: { xs: 1, sm: 2, md: 0 },
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Buscar pet"
                          variant="outlined"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          size={isSmallScreen ? "small" : "medium"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <SearchIcon
                                  fontSize={isSmallScreen ? "small" : "medium"}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Fab
                          variant={
                            isMobile || isSmallScreen ? undefined : "extended"
                          }
                          color="warning"
                          onClick={() => setModalAddPet(true)}
                          size={isSmallScreen ? "small" : "medium"}
                          sx={{
                            flexShrink: 0,
                          }}
                        >
                          <Add
                            sx={isMobile || isSmallScreen ? {} : { mr: 1 }}
                          />
                          {!isMobile && !isSmallScreen && "Adicionar Pet"}
                        </Fab>
                      </Box>
                    )}

                    <Box
                      sx={{
                        maxHeight: isSmallScreen
                          ? "calc(100vh - 250px)"
                          : isMobile
                            ? "calc(100vh - 280px)"
                            : "none",
                        minHeight: isSmallScreen
                          ? "calc(100vh - 250px)"
                          : isMobile
                            ? "calc(100vh - 280px)"
                            : "calc(100vh - 320px)",
                        overflowY:
                          isMobile || isSmallScreen ? "auto" : "visible",
                        px: { xs: 1, sm: 2, md: 0 },
                        pb: 2,
                      }}
                    >
                      <Grid
                        container
                        spacing={isSmallScreen ? 1 : 2}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent:
                            pets.length < 1
                              ? "center"
                              : { xs: "center", md: "flex-start" },
                          margin: 0,
                          width: "100%",
                        }}
                      >
                        {filteredPets.length > 0 ? (
                          <>
                            {paginatedPets.map((pet) => (
                              <Grid
                                key={pet._id}
                                size={{
                                  xs: 12,
                                  sm: 6,
                                  md: isSmallScreen ? 6 : 6,
                                  lg: 4,
                                }}
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "100%",
                                    maxWidth: isSmallScreen ? 320 : 400,
                                  }}
                                >
                                  <MiniPetCard
                                    data={pet}
                                    action={() => setPetDetails(true)}
                                    setData={() => setPetInfos(pet)}
                                    width={isSmallScreen ? "100%" : 340}
                                    height={isSmallScreen ? 160 : 180}
                                    imageSize={isSmallScreen ? 80 : 96}
                                    dense={isSmallScreen}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </>
                        ) : (
                          <EmptyState label="Nenhum pet cadastrado" />
                        )}
                      </Grid>
                    </Box>

                    {/* Paginação fixa abaixo dos cards */}
                    {filteredPets.length > 0 && totalPetsPages > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: isSmallScreen ? 2 : 3,
                          mb: 2,
                          px: { xs: 1, sm: 2, md: 0 },
                        }}
                      >
                        <Pagination
                          count={totalPetsPages}
                          page={petsPage}
                          onChange={(_, value) => setPetsPage(value)}
                          color="primary"
                          size={isMobile || isSmallScreen ? "small" : "large"}
                          showFirstButton={!(isMobile || isSmallScreen)}
                          showLastButton={!(isMobile || isSmallScreen)}
                          siblingCount={isMobile || isSmallScreen ? 0 : 1}
                          boundaryCount={isMobile || isSmallScreen ? 1 : 1}
                        />
                      </Box>
                    )}
                  </>
                )}

                {petDetails && (
                  <PetDetails
                    initialData={petInfos}
                    onClose={() => {
                      setPetDetails(false);
                      setPetInfos(null);
                    }}
                    onSave={() => {
                      fetchPets();
                      fetchAllPedidos();
                    }}
                    tecnico={session ? session.userERP : null}
                    openModalAddPet={() => setModalAddPet(true)}
                  />
                )}
              </Container>
            )}
            {currentSegment === "vendas" && (
              <Container>
                <Grid container spacing={2}>
                  <Grid
                    size={12}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      alignItems: "center",
                      maxWidth: "100%",
                      mb: { xs: 2, sm: isSmallScreen ? 2 : 3 },
                      px: { xs: 1, sm: 2, md: 0 },
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Filtrar vendas"
                      variant="outlined"
                      placeholder={
                        isMobile || isSmallScreen
                          ? ""
                          : "Ex: Maria, Ração, 456, 26/09/2025"
                      }
                      value={searchTermVendas}
                      onChange={(e) => setSearchTermVendas(e.target.value)}
                      size={isSmallScreen ? "small" : "medium"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon
                              fontSize={isSmallScreen ? "small" : "medium"}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Fab
                      variant={
                        isMobile || isSmallScreen ? undefined : "extended"
                      }
                      color="success"
                      onClick={() => setModalAddVenda(true)}
                      size={isSmallScreen ? "small" : "medium"}
                      sx={{
                        flexShrink: 0,
                      }}
                    >
                      <Add sx={isMobile || isSmallScreen ? {} : { mr: 1 }} />
                      {!isMobile && !isSmallScreen && "Nova Venda"}
                    </Fab>
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        maxHeight: isSmallScreen
                          ? "calc(100vh - 250px)"
                          : isMobile
                            ? "calc(100vh - 280px)"
                            : "520px",
                        minHeight: isSmallScreen
                          ? "calc(100vh - 250px)"
                          : isMobile
                            ? "calc(100vh - 280px)"
                            : "calc(100vh - 320px)",
                        overflowY: "auto",
                        px: { xs: 1, sm: 2, md: 0 },
                      }}
                    >
                      <Container sx={{ py: isSmallScreen ? 2 : 4 }}>
                        {session?.userERP.Tecnico ? (
                          filteredVendas.length > 0 ? (
                            <List>
                              {paginatedVendas.map((venda, index) => (
                                <ListItem
                                  key={`${venda.ID}-${index}`}
                                  sx={{
                                    py: isSmallScreen ? 1 : 2,
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar
                                      sx={{
                                        bgcolor: "success.main",
                                        width: isSmallScreen ? 32 : 40,
                                        height: isSmallScreen ? 32 : 40,
                                      }}
                                    >
                                      <Store
                                        fontSize={
                                          isSmallScreen ? "small" : "medium"
                                        }
                                      />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant={
                                          isSmallScreen ? "body2" : "body1"
                                        }
                                        fontWeight="medium"
                                      >
                                        {venda.Data}
                                      </Typography>
                                    }
                                    secondary={
                                      <>
                                        <Typography
                                          variant={
                                            isSmallScreen ? "caption" : "body2"
                                          }
                                          color="text.secondary"
                                        >
                                          {`${venda.Items?.[0]?.Descricao || "Produto"} - Código: ${venda.Codigo}`}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="success.main"
                                          sx={{ display: "block", mt: 0.5 }}
                                        >
                                          Cliente: {venda.Cliente}
                                        </Typography>
                                      </>
                                    }
                                    onClick={() => handleAbrirDetalhes(venda)}
                                  />
                                  <IconButton
                                    edge="end"
                                    aria-label="editar"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAbrirEdicao(venda);
                                    }}
                                    sx={{ ml: 1 }}
                                    size={isSmallScreen ? "small" : "medium"}
                                  >
                                    <Edit
                                      fontSize={
                                        isSmallScreen ? "small" : "medium"
                                      }
                                    />
                                  </IconButton>
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <EmptyState label="Nenhuma venda encontrada" />
                          )
                        ) : vendasUser.length > 0 ? (
                          <List>
                            {vendasUser.map((venda, index) => (
                              <ListItem
                                key={`${venda.ID}-${index}`}
                                sx={{
                                  py: isSmallScreen ? 1 : 2,
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    sx={{
                                      bgcolor: "success.main",
                                      width: isSmallScreen ? 32 : 40,
                                      height: isSmallScreen ? 32 : 40,
                                    }}
                                  >
                                    <Store
                                      fontSize={
                                        isSmallScreen ? "small" : "medium"
                                      }
                                    />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant={
                                        isSmallScreen ? "body2" : "body1"
                                      }
                                      fontWeight="medium"
                                    >
                                      {venda.Data}
                                    </Typography>
                                  }
                                  secondary={
                                    <>
                                      <Typography
                                        variant={
                                          isSmallScreen ? "caption" : "body2"
                                        }
                                        color="text.secondary"
                                      >
                                        {`${venda.Items?.[0]?.Descricao || "Produto"} - Código: ${venda.Codigo}`}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="success.main"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Cliente: {venda.Cliente}
                                      </Typography>
                                    </>
                                  }
                                  onClick={() => handleAbrirDetalhes(venda)}
                                />
                                <IconButton
                                  edge="end"
                                  aria-label="editar"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAbrirEdicao(venda);
                                  }}
                                  sx={{ ml: 1 }}
                                  size={isSmallScreen ? "small" : "medium"}
                                >
                                  <Edit
                                    fontSize={
                                      isSmallScreen ? "small" : "medium"
                                    }
                                  />
                                </IconButton>
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <EmptyState label="Nenhuma venda encontrada" />
                        )}
                      </Container>
                    </Box>

                    {/* Paginação fixa abaixo da lista */}
                    {session?.userERP.Tecnico &&
                      filteredVendas.length > 0 &&
                      totalVendasPages > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                            mb: 2,
                            px: { xs: 1, sm: 2, md: 0 },
                          }}
                        >
                          <Pagination
                            count={totalVendasPages}
                            page={vendasPage}
                            onChange={(_, value) => setVendasPage(value)}
                            color="primary"
                            size={isMobile || isSmallScreen ? "small" : "large"}
                            showFirstButton={!(isMobile || isSmallScreen)}
                            showLastButton={!(isMobile || isSmallScreen)}
                            siblingCount={isMobile || isSmallScreen ? 0 : 1}
                            boundaryCount={isMobile || isSmallScreen ? 1 : 1}
                          />
                        </Box>
                      )}
                  </Grid>
                </Grid>
              </Container>
            )}
            {currentSegment === "atendimentos" && (
              <Container>
                <Grid container spacing={2}>
                  {session?.userERP?.Tecnico && (
                    <Grid
                      size={12}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: { xs: 2, sm: isSmallScreen ? 2 : 3 },
                        px: { xs: 1, sm: 2, md: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          alignItems: "center",
                          maxWidth: "100%",
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Filtrar atendimentos"
                          variant="outlined"
                          placeholder={
                            isMobile || isSmallScreen
                              ? ""
                              : "Ex: João, Banho, 123, 26/09/2025"
                          }
                          value={searchTermAttach}
                          onChange={(e) => setSearchTermAttach(e.target.value)}
                          size={isSmallScreen ? "small" : "medium"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <SearchIcon
                                  fontSize={isSmallScreen ? "small" : "medium"}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Fab
                          variant={
                            isMobile || isSmallScreen ? undefined : "extended"
                          }
                          color="warning"
                          onClick={() => setModalAddAtendimento(true)}
                          size={isSmallScreen ? "small" : "medium"}
                          sx={{
                            flexShrink: 0,
                          }}
                        >
                          <Add
                            sx={isMobile || isSmallScreen ? {} : { mr: 1 }}
                          />
                          {!isMobile && !isSmallScreen && "Novo Atendimento"}
                        </Fab>
                      </Box>

                      {/* <FormControlLabel
                        control={
                          <Switch
                            checked={mostrarTodosAtendimentos}
                            onChange={(e) =>
                              setMostrarTodosAtendimentos(e.target.checked)
                            }
                            name="mostrarTodos"
                          />
                        }
                        label={
                          mostrarTodosAtendimentos
                            ? "Mostrando todos os pedidos"
                            : "Mostrando apenas atendimentos"
                        }
                      /> */}
                    </Grid>
                  )}
                  <Grid size={12}>
                    <Box
                      sx={{
                        maxHeight: isSmallScreen
                          ? "calc(100vh - 250px)"
                          : isMobile
                            ? "calc(100vh - 280px)"
                            : "520px",
                        minHeight: isSmallScreen
                          ? "calc(100vh - 250px)"
                          : isMobile
                            ? "calc(100vh - 280px)"
                            : "calc(100vh - 320px)",
                        overflowY: "auto",
                        px: { xs: 1, sm: 2, md: 0 },
                      }}
                    >
                      <Container sx={{ py: isSmallScreen ? 2 : 4 }}>
                        {session?.userERP.Tecnico ? (
                          filteredattachment.length > 0 ? (
                            <List>
                              {paginatedAttachment.map((venda, index) => (
                                <ListItem
                                  key={`${venda.ID}-${index}`}
                                  sx={{
                                    py: isSmallScreen ? 1 : 2,
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar
                                      sx={{
                                        bgcolor: "warning.main",
                                        width: isSmallScreen ? 32 : 40,
                                        height: isSmallScreen ? 32 : 40,
                                      }}
                                    >
                                      <Pets
                                        fontSize={
                                          isSmallScreen ? "small" : "medium"
                                        }
                                      />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant={
                                          isSmallScreen ? "body2" : "body1"
                                        }
                                        fontWeight="medium"
                                      >
                                        {venda.Data}
                                      </Typography>
                                    }
                                    secondary={
                                      <>
                                        <Typography
                                          variant={
                                            isSmallScreen ? "caption" : "body2"
                                          }
                                          color="text.secondary"
                                        >
                                          {`${venda.Items?.[0]?.Descricao || "Serviço"} - Código: ${venda.Codigo}`}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="warning.main"
                                          sx={{ display: "block", mt: 0.5 }}
                                        >
                                          Cliente: {venda.Cliente}
                                        </Typography>
                                      </>
                                    }
                                    onClick={() => handleAbrirDetalhes(venda)}
                                  />
                                  <IconButton
                                    edge="end"
                                    aria-label="editar"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAbrirEdicao(venda);
                                    }}
                                    sx={{ ml: 1 }}
                                    size={isSmallScreen ? "small" : "medium"}
                                  >
                                    <Edit
                                      fontSize={
                                        isSmallScreen ? "small" : "medium"
                                      }
                                    />
                                  </IconButton>
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <EmptyState label="Sem atendimentos" />
                          )
                        ) : attachmentUser.length > 0 ? (
                          <List>
                            {attachmentUser.map((venda, index) => (
                              <ListItem
                                key={`${venda.ID}-${index}`}
                                sx={{
                                  py: isSmallScreen ? 1 : 2,
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    sx={{
                                      bgcolor: "warning.main",
                                      width: isSmallScreen ? 32 : 40,
                                      height: isSmallScreen ? 32 : 40,
                                    }}
                                  >
                                    <Pets
                                      fontSize={
                                        isSmallScreen ? "small" : "medium"
                                      }
                                    />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant={
                                        isSmallScreen ? "body2" : "body1"
                                      }
                                      fontWeight="medium"
                                    >
                                      {venda.Data}
                                    </Typography>
                                  }
                                  secondary={
                                    <>
                                      <Typography
                                        variant={
                                          isSmallScreen ? "caption" : "body2"
                                        }
                                        color="text.secondary"
                                      >
                                        {`${venda.Items?.[0]?.Descricao || "Serviço"} - Código: ${venda.Codigo}`}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="warning.main"
                                        sx={{ display: "block", mt: 0.5 }}
                                      >
                                        Cliente: {venda.Cliente}
                                      </Typography>
                                    </>
                                  }
                                  onClick={() => handleAbrirDetalhes(venda)}
                                />
                                <IconButton
                                  edge="end"
                                  aria-label="editar"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAbrirEdicao(venda);
                                  }}
                                  sx={{ ml: 1 }}
                                  size={isSmallScreen ? "small" : "medium"}
                                >
                                  <Edit
                                    fontSize={
                                      isSmallScreen ? "small" : "medium"
                                    }
                                  />
                                </IconButton>
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <EmptyState label="Sem atendimentos" />
                        )}
                      </Container>
                    </Box>

                    {/* Paginação fixa abaixo da lista */}
                    {session?.userERP.Tecnico &&
                      filteredattachment.length > 0 &&
                      totalAttachmentPages > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                            mb: 2,
                            px: { xs: 1, sm: 2, md: 0 },
                          }}
                        >
                          <Pagination
                            count={totalAttachmentPages}
                            page={atendimentosPage}
                            onChange={(_, value) => setAtendimentosPage(value)}
                            color="primary"
                            size={isMobile || isSmallScreen ? "small" : "large"}
                            showFirstButton={!(isMobile || isSmallScreen)}
                            showLastButton={!(isMobile || isSmallScreen)}
                            siblingCount={isMobile || isSmallScreen ? 0 : 1}
                            boundaryCount={isMobile || isSmallScreen ? 1 : 1}
                          />
                        </Box>
                      )}
                  </Grid>
                </Grid>
              </Container>
            )}{" "}
            {currentSegment === "agenda" && <AgendaPage />}
            {currentSegment === "servicos" && <ServicosEmAndamentoPage />}
            {currentSegment === "notifications" && (
              <Container>
                <NotificationsPage
                  isMobile={isMobile}
                  isSmallScreen={isSmallScreen}
                />
              </Container>
            )}
            {currentSegment === "perfil" && (
              <Box
                sx={{
                  maxHeight: isSmallScreen
                    ? "calc(100vh - 200px)"
                    : isMobile
                      ? "calc(100vh - 200px)"
                      : "none",
                  overflowY: isMobile || isSmallScreen ? "auto" : "visible",
                  width: "100%",
                  px: { xs: 1, sm: 2, md: 0 },
                  py: { xs: 2, sm: 2 },
                }}
              >
                <Grid container spacing={2} display={"flex"}>
                  <UserProfile data={session ? session?.userERP : null} />
                </Grid>
              </Box>
            )}
            <PetModal
              open={modalAddPet}
              onClose={() => setModalAddPet(false)}
              onSave={() => fetchPets()}
              tutorId={
                session?.userERP.Tecnico ? petInfos?.tutor._id : session?.userId
              }
            />
            <ModalNewAtendimento
              open={modalAddAtendimento}
              onClose={() => setModalAddAtendimento(false)}
              onSave={() => {
                fetchAllPedidos();
              }}
              tecnico={session?.userERP || null}
            />
            <ModalNewVenda
              open={modalAddVenda}
              onClose={() => setModalAddVenda(false)}
              onSave={() => {
                fetchAllPedidos();
              }}
              tecnico={session?.userERP || null}
            />
            <ModalDetalhesPedido
              open={modalDetalhesPedido}
              onClose={handleFecharDetalhes}
              pedido={pedidoSelecionado}
            />
            <ModalEditarPedido
              open={modalEditarPedido}
              onClose={handleFecharEdicao}
              pedido={pedidoSelecionado}
              onSave={() => {
                fetchAllPedidos();
                handleFecharEdicao();
              }}
            />
            {/* Conteúdo comum a todas as páginas pode ficar aqui */}
            <Grid spacing={12} sx={{ backgroundColor: purple[100] }}>
              <Skeleton height={14} />
            </Grid>
          </Grid>
        </PageContainer>
      </DashboardLayout>
    </AppProvider>
  );
}
